import { IStoreState } from '../types';
import { createSelector } from 'reselect';
import { chain, filter, find, orderBy, partial } from 'lodash';
import { dateOrderResolver, getCollectionImageURL } from '../utils';
import { IImage, ISortGallery } from '../reducers';
import naturalCompare from 'natural-compare-lite';

// Collections for which duplicate images (by original name) should be removed
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const COLLECTIONS_WITH_DEDUP = new Set<string>([
  'SkcrQcQSZe',
  'H1yrwJD-Wl',
  'H1WdL-n3xe',
  'ry_czUz-lg',
  'S1m_9k6Ull',
  'Hy11HwT2lg',
  'B1x6hX9Tgg',
  'BJ1UFG_tlg',
  'rkyNRw06eg',
  'BktPM8txkx',
  'rk553Alkbl'
]);

export const getImagesList = (state: IStoreState) => state.collectionImages.items;
export const getImagesToBatchUpdate = (state: IStoreState) => state.collectionImages.itemsToUpdate;
export const getUploadSessionList = (state: IStoreState) =>
  state.collectionImages.uploadSessionImages;
export const getUploadPauseState = (state: IStoreState) => state.collectionImages.uploadIsPaused;
export const getUploadedImagesList = createSelector(getImagesList, (images) =>
  images.filter(({ status, isCollectionHeader }) => status === 'uploaded' && !isCollectionHeader)
);
export const getSearchImageString = (state: IStoreState) => state.collectionImages.searchImage;
// Collection header should reflect the real number of uploaded photos in the collection,
// without any artificial de-duplication by name.
export const getTotalImagesCount = createSelector(
  getImagesList,
  (images) =>
    images.filter(({ status, isCollectionHeader }) => status === 'uploaded' && !isCollectionHeader)
      .length
);
export const getTotalImagesCountForWatermark = createSelector(
  getImagesList,
  (images) =>
    images.filter(
      ({ status, isCollectionHeader, extension }) =>
        status === 'uploaded' && !isCollectionHeader && extension?.toLowerCase() !== 'gif'
    ).length
);
export const getResetImagesFilterState = (state: IStoreState) =>
  state.collectionImages.resetSelectionsFilter;

export const getImagesUploadedStatus = (state: IStoreState) =>
  state.collectionImages.items.every((image) => image.status === 'uploaded');

export const getAppIcon = (state: IStoreState) => state.collectionImages.appIcon;

export const getGalleryTitleImage = createSelector(
  [getImagesList, getSearchImageString],
  (images) =>
    (gallery_id: string): IImage | undefined =>
      find(images, { gallery_id, isTitleImage: true })
);

export const getGallerySmallScreenTitleImage = createSelector(
  [getImagesList, getSearchImageString],
  (images) => (gallery_id: string) => find(images, { gallery_id, isSmallScreenTitleImage: true })
);

// const removeDuplicateImagesByName = (images: IImage[]): IImage[] =>
//   images.reduce((acc, current) => {
//     const existing = acc.find((img) => img.originalImageName === current.originalImageName);
//     if (!existing) {
//       acc.push(current);
//     } else {
//       // Keep the one with the latest uploadedAt date
//       const currentDate = new Date(current.uploadedAt);
//       const existingDate = new Date(existing.uploadedAt);
//       if (currentDate > existingDate) {
//         const index = acc.indexOf(existing);
//         acc[index] = current;
//       }
//     }

//     return acc;
//   }, [] as IImage[]);

export const formatImagesForPhotoswipe = (images: IImage[], signedWilCardUrl: string) =>
  images.map((image) => ({
    ...image,
    image,
    msrc: getCollectionImageURL(image, 'M', false, signedWilCardUrl),
    src: getCollectionImageURL(image, 'L', false, signedWilCardUrl),
    w: image.width || 0,
    h: image.height || 0
  }));

// A doc the grid can render as an actual photo (thumbnails exist).
export const isRenderableImage = (img: IImage) =>
  img.status === 'uploaded' || img.compressionStatus === 'running';

// A backend doc whose upload died (offline, closed tab) — it stays until the
// user deletes it manually (SBK-6252). Local session placeholders and service
// docs (header/icon) have their own lifecycle and are excluded.
export const isFailedUploadDoc = (img: IImage) =>
  img.status === 'uploading' &&
  !img.uploadingStatus &&
  !img.isCollectionHeader &&
  !img.isIcon &&
  !(img._id || '').startsWith('uploading_image_');

export const sortImages = (images: IImage[], sortBy?: string, sortOrder?: 'asc' | 'desc') => {
  const list = [...images];
  if (sortBy === 'originalImageName') {
    list.sort((a: any, b: any) => naturalCompare(a.originalImageName, b.originalImageName));
    if (sortOrder === 'desc') {
      list.reverse();
    }

    return list;
  }
  const uploadedImages = filter(
    list,
    (img) => img.status === 'uploaded' || img.compressionStatus === 'running'
  );
  const inUploadImages = filter(
    list,
    (listItem) =>
      listItem.uploadingStatus &&
      ['uploading', 'compressing', 'pre-upload'].indexOf(listItem.uploadingStatus) > -1
  );
  // "Upload order" / "Randomise" both sort by a persisted numeric sequence
  // (uploadOrder / randomOrder). Images that predate the field come first,
  // ordered by uploadedAt. See SBK-9036.
  const sequenceField = sortBy === 'uploadOrder' || sortBy === 'randomOrder' ? sortBy : null;
  const orderImages = (items: IImage[]) =>
    sequenceField
      ? orderBy(
          items,
          [
            (img: IImage) => (typeof img[sequenceField] === 'number' ? 1 : 0),
            (img: IImage) =>
              typeof img[sequenceField] === 'number'
                ? (img[sequenceField] as number)
                : new Date(img.uploadedAt).getTime()
          ],
          [sortOrder || 'asc', sortOrder || 'asc']
        )
      : orderBy(
          items,
          partial(dateOrderResolver, sortBy),
          sortBy !== 'manualOrder' ? sortOrder : ['asc']
        );

  // failed uploads go in a stable block at the end, newest first
  const failedUploadImages = orderBy(
    filter(list, isFailedUploadDoc),
    (img) => new Date(img.uploadedAt).getTime(),
    'desc'
  );

  const orederedUploadedImages = orderImages(uploadedImages);
  const orederedInUploadImages = orderImages(inUploadImages);

  return [...orederedUploadedImages, ...orederedInUploadImages, ...failedUploadImages];
};

// Helper function to process gallery images with common logic
const processGalleryImages = (
  images: IImage[],
  search: string,
  galleries: ISortGallery[],
  excludeGifs: boolean = false,
  includeFailedUploads: boolean = false,
  uploadSessionImages: Record<string, IImage[]> = {}
) => {
  const galleriesImages: IImage[] = [];

  galleries.forEach(({ gallery_id, sortBy, sortOrder }) => {
    // hide failed docs while the gallery is mid-session — a refetch brings the
    // in-flight docs into the store and they'd duplicate the placeholders
    const showFailedUploads =
      includeFailedUploads && !(uploadSessionImages[gallery_id] || []).length;
    const galleryImages = chain(images)
      .filter(
        (img) =>
          img.gallery_id === gallery_id &&
          (isRenderableImage(img) || (showFailedUploads && isFailedUploadDoc(img))) &&
          (!excludeGifs || img.extension.toLowerCase() !== 'gif') &&
          (!search.length || img.originalImageName.toLowerCase().includes(search.toLowerCase()))
      )
      .thru((list) => {
        if (sortBy === 'originalImageName') {
          list.sort((a, b) => naturalCompare(a.originalImageName, b.originalImageName));
          if (sortOrder === 'desc') list.reverse();

          return list;
        }

        return sortImages(list, sortBy, sortOrder);
      })
      .value();

    galleriesImages.push(...galleryImages);
  });

  return galleriesImages;
};

export const getGalleryImagesByGalleryID = createSelector(
  [getImagesList, getSearchImageString, getUploadSessionList],
  (images, search, uploadSessionImages) =>
    (gallery_id: string, sortBy?: string, sortOrder?: 'asc' | 'desc') => {
      const singleGallery = [{ gallery_id, sortBy, sortOrder }];

      return processGalleryImages(images, search, singleGallery, false, true, uploadSessionImages);
    }
);

export const getGalleryImagesByGalleryIDs = createSelector(
  [getImagesList, getSearchImageString, getUploadSessionList],
  (images, search, uploadSessionImages) => (galleries: ISortGallery[]) =>
    processGalleryImages(images, search, galleries, false, true, uploadSessionImages)
);

// Specific selector for slideshow modal that filters out GIFs to match CollectionAlbum behavior
export const getGalleryImagesByGalleryIDsForSlideshow = createSelector(
  [getImagesList, getSearchImageString],
  (images, search) => (galleries: ISortGallery[]) =>
    processGalleryImages(images, search, galleries, true)
);

export const getImagesByGalleryID = createSelector(
  [getImagesList],
  (images) => (gallery_id: string) => chain(images).filter({ gallery_id }).value()
);

export const getImageByID = createSelector(
  getImagesList,
  (images) => (_id: string) => find(images, { _id }) || {}
);

export const getSelectedImagesIDs = (state: IStoreState) => state.collectionImages.selected;

export const getIsCropped = (state: IStoreState) => state.collectionImages.isCropped;

export const getGalleryImagesDuplicateIDS = createSelector(
  getImagesByGalleryID,
  (getImagesByGalleryIDSelector) =>
    (galleryID: string, files: File[], uploadSessionImages: IImage[]) => {
      const images = getImagesByGalleryIDSelector(galleryID);
      const allImages = [...images, ...uploadSessionImages];
      const filesNames = new Set(files.map((file) => file.name));

      // If the gallery contains several images with the same name, replace only
      // the latest one (by uploadedAt/createdAt). The older copies stay put.
      const latestByName = new Map<string, IImage>();

      allImages.forEach((image) => {
        if (!filesNames.has(image.originalImageName) || image.gallery_id !== galleryID) return;

        const current = latestByName.get(image.originalImageName);
        const imageTime = new Date(image.uploadedAt || image.createdAt || 0).getTime();
        const currentTime = current
          ? new Date(current.uploadedAt || current.createdAt || 0).getTime()
          : -Infinity;

        if (!current || imageTime >= currentTime) {
          latestByName.set(image.originalImageName, image);
        }
      });

      return Array.from(latestByName.values()).map((image) => ({
        id: image._id,
        name: image.originalImageName
      }));
    }
);

export const getImagesByGalleriesUsingGalleryIDs = createSelector(
  [getGalleryImagesByGalleryIDs],
  (getFilteredSortedImages) => (galleries: ISortGallery[]) => {
    const images = getFilteredSortedImages(galleries);

    return images.reduce<Record<string, IImage[]>>((acc, image) => {
      if (!acc[image.gallery_id]) {
        acc[image.gallery_id] = [];
      }

      acc[image.gallery_id].push(image);

      return acc;
    }, {});
  }
);

export const groupImagesByGalleries = (sortedImages: IImage[]) =>
  sortedImages.reduce<Record<string, IImage[]>>((acc, image) => {
    if (!acc[image.gallery_id]) {
      acc[image.gallery_id] = [];
    }

    acc[image.gallery_id].push(image);

    return acc;
  }, {});

export const getCollectionHeaderImage = createSelector(
  [getImagesList],
  (images) =>
    images.find((image) => image._collection && image.isCollectionHeader) ||
    images.find((image) => image.isCollectionHeader)
);

export const getFullscreenState = (state: IStoreState) => state.collectionImages.fullscreen;
export const getFullscreenDataState = (state: IStoreState) => state.collectionImages.fullscreenData;
export const getDeletionStatus = (state: IStoreState) =>
  state.collectionImages.imageDeletionInProcess;
